import type {
  ActionUndoneMessage,
  MoveSnackbarMessage,
  RenameSnackbarMessage,
  CreatedSnackbarMessage,
  DeletedSnackbarMessage,
  AccessGrantedSnackbarMessage,
  AccessRemovedSnackbarMessage,
  ErrorSnackbarMessage,
  AgreementsApprovedSnackbarMessage,
  InfoSnackbarMessage,
  OfflineMessage,
  SoftLeaseMessage,
  TemplateMessage,
  MakeACopyMessage,
  FileSavedMessage,
  FileApprovedMessage,
  FileArchivedMessage,
  FileRestoredMessage,
  AllArchivedDeletedMessage,
  ToastScope,
} from 'store/snackbar/types';
import { MessageType } from 'store/snackbar/types';
import type { StorageUnit } from 'store/storageunits/types';
import { splitName, uppercase } from 'util/nameHelper';
import { isProject } from 'util/isProjectHelper';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { ReactComponent as CheckIcon } from 'icons/checkOutlined.svg';
import { ReactComponent as ApprovedIcon } from 'icons/approvedFiles.svg';
import { routes } from 'util/routes';

let toastSequence = 0;

const createToastId = () => `toast-${Date.now()}-${toastSequence++}`;

const getCurrentToastScope = (): ToastScope => {
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith(routes.file(''))
  ) {
    return 'designer';
  }

  return 'explorer';
};

const createToastMeta = (
  scope: ToastScope = getCurrentToastScope(),
  closable = true,
) => ({
  id: createToastId(),
  scope,
  closable,
});

export const storageUnitRenamed = (
  unit: StorageUnit,
  oldUnit: StorageUnit,
): RenameSnackbarMessage => ({
  ...createToastMeta(),
  title: `"${splitName(oldUnit.type, oldUnit.name)}" renamed as "${splitName(
    unit.type,
    unit.name,
  )}"`,
  type: MessageType.RENAME,
  data: { unit, oldUnit },
});

export const storageUnitMoved = (
  unit: StorageUnit,
  newParentId?: string,
  oldParentId?: string,
): MoveSnackbarMessage => ({
  ...createToastMeta(),
  title: `"${splitName(unit.type, unit.name)}" was moved`,
  type: MessageType.MOVE,
  data: { unit, newParentId, oldParentId },
});

export const storageUnitCreated = (
  unit: StorageUnit,
  messageType: MessageType.CREATED | MessageType.SAVE_AS,
  options?: { undoable?: boolean },
): CreatedSnackbarMessage => {
  const displayType = isProject(unit) ? 'Project' : uppercase(unit.type);
  return {
    ...createToastMeta(),
    title: `${displayType} "${splitName(unit.type, unit.name)}" was created`,
    type: messageType,
    data: { unit, undoable: options?.undoable },
  };
};

export const storageUnitDeleted = (
  unit: StorageUnit,
): DeletedSnackbarMessage => {
  const displayType = isProject(unit) ? 'Project' : uppercase(unit.type);
  return {
    ...createToastMeta(),
    title: `${displayType} "${splitName(unit.type, unit.name)}" was deleted`,
    type: MessageType.DELTED,
    data: { unit },
  };
};

export const storageUnitAccessGranted = (): AccessGrantedSnackbarMessage => ({
  ...createToastMeta(),
  title: `Access granted`,
  type: MessageType.ACCESS_GRANTED,
  data: {},
});

export const storageUnitAccessRemoved = (): AccessRemovedSnackbarMessage => ({
  ...createToastMeta(),
  title: `Access removed`,
  type: MessageType.ACCESS_REMOVED,
  data: {},
});

export const actionUndone = (): ActionUndoneMessage => ({
  ...createToastMeta(),
  title: 'Action undone',
  type: MessageType.UNDO,
  data: {},
});

export const agreementsApproved = (): AgreementsApprovedSnackbarMessage => ({
  ...createToastMeta(),
  title: 'All agreements have been approved',
  type: MessageType.AGREEMENT_APPROVED,
  data: {},
});

export const offlineMessage = (): OfflineMessage => ({
  ...createToastMeta(),
  title: 'You are offline',
  type: MessageType.OFFLINE,
  permanent: true,
  position: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  data: {},
  icon: CloudOffIcon,
});

export const infoMessage = (title: string): InfoSnackbarMessage => ({
  ...createToastMeta(),
  title,
  type: MessageType.INFO,
  data: {},
});

export const designerOfflineMessage = (): InfoSnackbarMessage => ({
  ...createToastMeta('designer'),
  title: 'You are working offline. PD cannot perform any simulations.',
  type: MessageType.INFO,
  position: {
    vertical: 'top',
    horizontal: 'left',
  },
  data: {},
});

export const errorMessage = (title: string): ErrorSnackbarMessage => ({
  ...createToastMeta(),
  title,
  type: MessageType.ERROR,
  data: {},
  error: true,
});

export const templateMessage = (
  id: string,
  name: string,
  permanent: boolean,
): TemplateMessage => ({
  ...createToastMeta('designer'),
  title: 'This is a template flowsheet.',
  type: MessageType.TEMPLATE,
  position: {
    vertical: 'top',
    horizontal: 'left',
  },
  data: {
    id,
    name,
  },
  permanent,
});

export const makeACopyMessage = (
  id: string,
  name: string,
  lockedBy?: string,
  options?: { useConflictDraft?: boolean },
): MakeACopyMessage => ({
  ...createToastMeta(getCurrentToastScope(), false),
  title: lockedBy
    ? `View only. Locked by ${lockedBy}.`
    : 'View only. Another person is working on the file.',
  type: MessageType.MAKE_A_COPY,
  position: {
    vertical: 'top',
    horizontal: 'left',
  },
  data: {
    id,
    name,
    lockedBy,
    useConflictDraft: options?.useConflictDraft,
  },
  permanent: true,
});

export const softLeaseMessage = (
  id: string,
  name: string,
  userNames: string[],
): SoftLeaseMessage => {
  const formattedNames =
    userNames.length === 1
      ? `${userNames[0]} currently has this file open.`
      : `${userNames.join(', ')} currently have this file open.`;
  return {
    ...createToastMeta('designer'),
    title: formattedNames,
    type: MessageType.SOFT_LEASE,
    position: {
      vertical: 'top',
      horizontal: 'left',
    },
    data: {
      id,
      name,
    },
    permanent: true,
  };
};

type FileSavedOptions = {
  isLocal?: boolean;
};

export const fileSavedMessage = (
  options: FileSavedOptions = {},
): FileSavedMessage => ({
  ...createToastMeta(),
  title: options.isLocal
    ? 'File saved locally. Connect to internet to sync.'
    : 'File saved.',
  type: MessageType.FILE_SAVED,
  position: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  icon: CheckIcon,
  data: {},
});

export const fileApprovedMessage = (): FileApprovedMessage => ({
  ...createToastMeta(),
  title: 'File approved.',
  type: MessageType.FILE_APPROVED,
  position: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  icon: ApprovedIcon,
  data: {},
});

export const fileArchivedMessage = (id: string): FileArchivedMessage => ({
  ...createToastMeta(),
  title: 'Moved to bin',
  type: MessageType.ARCHIVED,
  data: { id },
});

export const fileRestoredMessage = (
  unit: StorageUnit,
): FileRestoredMessage => ({
  ...createToastMeta(),
  title: `"${splitName(unit.type, unit.name)}" was restored`,
  type: MessageType.RESTORED,
  data: {},
});

export const allArchivedDeletedMessage = (): AllArchivedDeletedMessage => ({
  ...createToastMeta(),
  title: 'Bin emptied',
  type: MessageType.ARCHIVED_DELETED,
  data: {},
});

export const workflowValuesInherited = (): InfoSnackbarMessage => ({
  ...createToastMeta(),
  title: 'Workflow values updated with project values',
  type: MessageType.INFO,
  data: {},
});
